import { useState } from 'react';
import type React from 'react';

import { Button, Link, Text } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core/react/i18n';
import { useKnownAvailableParams, useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import type { Endorsement } from '../../api';
import { useDeleteEndorsement, useEndorsements } from '../../hooks';
import { EndorsementCardEdit } from '../EndorsementCardEdit';

import { DeleteEndorsementModal } from './components/DeleteEndorsementModal';
import {
	actionButtonsContainer,
	descriptionContainer,
	endorsementCardContainer,
	sectionContainer,
	titleContainer,
} from './EndorsementSection.styles';

type EndorsementListProps = Readonly<{
	endorsements: readonly Endorsement[];
	isContentExpanded: boolean;
	onToggleExpansion: () => void;
	onEdit: (id: string) => void;
	onRemove: (id: string) => void;
}>;

type EndorsementItemProps = Readonly<{
	endorsement: Endorsement;
	isContentExpanded: boolean;
	onToggleExpansion: () => void;
	onEdit: (id: string) => void;
	onRemove: (id: string) => void;
}>;

const getEndorsementDescription = (content: string | null | undefined, isContentExpanded: boolean): string => {
	const fullContent = content ?? '';
	const contentLength = fullContent.length;
	const hasLongContent = contentLength > 200;
	const shouldTruncate = hasLongContent && !isContentExpanded;

	return shouldTruncate ? `${fullContent.slice(0, 200)}...` : fullContent;
};

const EndorsementItem: React.FC<EndorsementItemProps> = ({
	endorsement,
	isContentExpanded,
	onToggleExpansion,
	onEdit,
	onRemove,
}) => {
	const contentLength = endorsement.content?.length ?? 0;
	const shouldShowReadMore = contentLength > 200;
	const displayedDescription = getEndorsementDescription(endorsement.content, isContentExpanded);
	const readMoreButtonLabel = isContentExpanded ? 'View less' : 'Read full endorsement';

	return (
		<EndorsementCardEdit
			key={endorsement.id}
			name={endorsement.name}
			title={endorsement.title ?? ''}
			description={displayedDescription}
			photoUrl={endorsement.photo?.userSmall.url ?? ''}
			link={endorsement.url ?? '#'}
			onEdit={() => onEdit(endorsement.id)}
			onRemove={() => onRemove(endorsement.id)}
			onReadMore={shouldShowReadMore ? onToggleExpansion : undefined}
			readMoreLabel={shouldShowReadMore ? readMoreButtonLabel : undefined}
			className="bg-background-base border-stroke-weak rounded-lg border"
		/>
	);
};

const EndorsementList: React.FC<EndorsementListProps> = ({
	endorsements,
	isContentExpanded,
	onToggleExpansion,
	onEdit,
	onRemove,
}) => {
	return (
		<div className={endorsementCardContainer()}>
			{endorsements.map((endorsement) => (
				<EndorsementItem
					key={endorsement.id}
					endorsement={endorsement}
					isContentExpanded={isContentExpanded}
					onToggleExpansion={onToggleExpansion}
					onEdit={onEdit}
					onRemove={onRemove}
				/>
			))}
		</div>
	);
};

export const EndorsementSection: React.FC = () => {
	const { slug } = useKnownAvailableParams<{ slug: string }>();
	const navigate = useNavigate();
	const track = useTracking();
	const [isContentExpanded, setIsContentExpanded] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [endorsementToDelete, setEndorsementToDelete] = useState<string | null>(null);

	const {
		data: { petitionId, endorsements },
		actions: { refetch },
	} = useEndorsements({ petitionSlugOrId: slug });

	const {
		data: { isDeleting },
		actions: { deleteEndorsement: deleteEndorsementAction },
	} = useDeleteEndorsement({
		petitionId,
		onSuccess: async () => {
			setIsDeleteModalOpen(false);
			setEndorsementToDelete(null);
			await refetch();
		},
	});

	const handleAddAnother = () => {
		void track('add_petition_endorsement');
		navigate(`/p/${slug}/dashboard/endorsements/add`);
	};

	const handleViewOnPetition = () => {
		// TODO: Navigate to petition page
		// eslint-disable-next-line no-console
		console.log('View on petition page clicked');
	};

	const handleEdit = (endorsementId: string) => {
		void track('edit_petition_endorsement');
		navigate(`/p/${slug}/dashboard/endorsements/edit/${endorsementId}`);
	};

	const handleRemove = (endorsementId: string) => {
		void track('delete_petition_endorsement');
		setEndorsementToDelete(endorsementId);
		setIsDeleteModalOpen(true);
	};

	const handleSeeExampleClick = () => {
		void track('petition_manage_endorsements_example_click');
	};

	const handleConfirmDelete = async () => {
		if (!endorsementToDelete) return;
		await deleteEndorsementAction(endorsementToDelete);
	};

	const handleToggleContentExpansion = () => {
		setIsContentExpanded((prev) => !prev);
	};

	if (endorsements.length === 0) {
		return null;
	}

	return (
		<div className={sectionContainer()} data-testid="endorsement-section" id="endorsements">
			{/* Header */}
			<div className={titleContainer()}>
				<Text variant="heading" size="x-small" fontWeight="bold" className="text-typography-strong">
					<Translate value="corgi.petition.edit.endorsements.title" />
				</Text>
			</div>

			{/* Description */}
			<div className={descriptionContainer()}>
				<Text variant="body" className="text-typography-strong">
					<Translate value="corgi.petition.edit.endorsements.description" />{' '}
					<Link href="#" variant="secondary" className="underline" onClick={handleSeeExampleClick}>
						<Translate value="corgi.petition.edit.endorsements.see_example" />
					</Link>
				</Text>
			</div>

			{/* Endorsement Cards */}
			<EndorsementList
				endorsements={endorsements}
				isContentExpanded={isContentExpanded}
				onToggleExpansion={handleToggleContentExpansion}
				onEdit={handleEdit}
				onRemove={handleRemove}
			/>

			{/* Action Buttons */}
			<div className={actionButtonsContainer()}>
				<Button
					onClick={handleAddAnother}
					variant="secondary"
					size="x-small"
					data-testid="add-another-endorsement-button"
				>
					<Translate value="corgi.petition.edit.endorsements.add_another_button" />
				</Button>
				<Button onClick={handleViewOnPetition} variant="tertiary" size="x-small" data-testid="view-on-petition-button">
					<Translate value="corgi.petition.edit.endorsements.view_on_petition_button" />
				</Button>
			</div>

			{/* Delete Confirmation Modal */}
			<DeleteEndorsementModal
				isOpen={isDeleteModalOpen}
				onOpenChange={setIsDeleteModalOpen}
				onConfirmDelete={handleConfirmDelete}
				isDeleting={isDeleting}
			/>
		</div>
	);
};
